import { useState } from 'react';

export interface FormData {
  customerName: string;
  accountCSN: string;
  serverDeviceID: string;
  contractEndDate: string;
  isUSDoD: string;
  impactLevel: string;
  version: string;
  build: string;
  settingsValue: {
    disableProductUsage: boolean;
    disconnected: boolean;
    disableServiceSystem: boolean;
    readOnly: boolean;
    disableGenuineCompliance: boolean;
    disableLicensingCompliance: boolean;
    fedRamp: boolean;
  };
}

export interface FieldErrors {
  customerName: string;
  accountCSN: string;
  serverDeviceID: string;
  contractEndDate: string;
  isUSDoD: string;
  impactLevel: string;
}

export interface TouchedFields {
  customerName: boolean;
  accountCSN: boolean;
  serverDeviceID: boolean;
  contractEndDate: boolean;
  isUSDoD: boolean;
  impactLevel: boolean;
}

export const useFormValidation = () => {
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
    customerName: '',
    accountCSN: '',
    serverDeviceID: '',
    contractEndDate: '',
    isUSDoD: '',
    impactLevel: ''
  });

  const [touchedFields, setTouchedFields] = useState<TouchedFields>({
    customerName: false,
    accountCSN: false,
    serverDeviceID: false,
    contractEndDate: false,
    isUSDoD: false,
    impactLevel: false
  });

  const validateField = (fieldName: string, value: string, formData: FormData) => {
    if (!value.trim()) {
      const errorMessages: Record<string, string> = {
        customerName: 'Enter name of the Account',
        accountCSN: 'Enter valid CSN',
        serverDeviceID: 'Enter valid Server ID generated from AdskDeviceSettingGenerator Tool',
        contractEndDate: 'Enter valid contract end date',
        isUSDoD: 'Select a response',
        impactLevel: 'Select applicable Impact level'
      };
      return errorMessages[fieldName] || `${fieldName} is required`;
    }
    
    // Account Name specific validation
    if (fieldName === 'customerName') {
      // Check for invalid characters (forward slash, backslash, semicolon, quotes)
      const invalidChars = /[/\\;"']/;
      if (invalidChars.test(value)) {
        return 'Only alphanumeric characters, spaces, hyphens (-), and underscores (_) are allowed';
      }
      if (value.length > 100) {
        return 'Maximum 100 characters allowed';
      }
    }
    
    // Account CSN length validation
    if (fieldName === 'accountCSN') {
      if (value.length > 100) {
        return 'Maximum 100 characters allowed';
      }
    }
    
    // Server Device ID specific validation
    if (fieldName === 'serverDeviceID') {
      // Check that value contains exactly one '&' character
      const ampersandCount = (value.match(/&/g) || []).length;
      if (ampersandCount !== 1) {
        return 'Invalid Server Device ID entered';
      }
      
      // Check that '&' is not at the beginning or end, and has content on both sides
      const parts = value.split('&');
      if (parts.length !== 2 || parts[0].trim() === '' || parts[1].trim() === '') {
        return 'Invalid Server Device ID entered';
      }

      // Check that the total length is exactly 129 characters
      if (value.length !== 129) {
        return 'Invalid Server Device ID entered';
      }
    }
    
    if (fieldName === 'contractEndDate') {
      const selectedDate = new Date(value);
      selectedDate.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
      
      if (selectedDate <= today) {
        return 'Contract end date must be in the future';
      }
    }
    
    if (fieldName === 'impactLevel') {
      if (formData.isUSDoD === 'yes' && !value.trim()) {
        return 'Select applicable Impact level';
      }
    }
    
    return '';
  };

  const handleFieldBlur = (fieldName: string, formData: FormData) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    
    const value = formData[fieldName as keyof FormData] as string;
    const error = validateField(fieldName, value, formData);
    setFieldErrors(prev => ({ ...prev, [fieldName]: error }));
  };

  const clearFieldError = (fieldName: string) => {
    if (fieldErrors[fieldName as keyof FieldErrors]) {
      setFieldErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const markAllFieldsTouched = () => {
    setTouchedFields({
      customerName: true,
      accountCSN: true,
      serverDeviceID: true,
      contractEndDate: true,
      isUSDoD: true,
      impactLevel: true
    });
  };

  return {
    fieldErrors,
    touchedFields,
    validateField,
    handleFieldBlur,
    clearFieldError,
    markAllFieldsTouched,
    setFieldErrors,
    setTouchedFields
  };
};

